import moment, { Moment } from 'moment-timezone';

export const DEFAULT_DISPLAY_TIME_ZONE = 'UTC';

/**
 * Resolve the IANA zone used to display a device's data.
 * Falls back to UTC when the device has no zone, or the name is not a known IANA zone, so that
 * placeholder values are never passed to `moment.tz()` / `.tz()`.
 */
export const getDisplayTimeZone = (timeZoneName?: string | null): string => {
  return timeZoneName && moment.tz.zone(timeZoneName) ? timeZoneName : DEFAULT_DISPLAY_TIME_ZONE;
};

const getDailyParamsFromLocalNow = (localNow: Moment, days: number) => ({
  from: moment.utc(localNow).startOf('day').subtract(days - 1, 'days').valueOf(),
  to: moment.utc(localNow).endOf('hour').valueOf()
});

const getHourlyParamsFromLocalNow = (localNow: Moment, days: number) => ({
  from: moment.utc(localNow).startOf('hour').subtract((24 * days) - 1, 'hours').valueOf(),
  to: moment.utc(localNow).endOf('hour').valueOf()
});

/**
 * The wall-clock reading of a real UTC instant in an IANA zone, expressed as a UTC Moment - the
 * representation of the `local` field. The zone is applied to the instant itself, so the offset
 * in force at that instant (including across DST transitions) is always the correct one.
 */
export const getZoneWallClock = (now: Moment, timeZone: string): Moment => {
  return moment.utc(moment.utc(now).tz(timeZone).format('YYYY-MM-DD[T]HH:mm:ss.SSS'));
};

/** Return `from` and `to` params for relative queries in device local time
 * for data showing daily data, where we are looking for x days worth of data including today
 */
export const getRelativeDailyDeviceDateParams = (now: Moment, timeZoneOffset: number | null, days: number) => {
  return getDailyParamsFromLocalNow(moment.utc(now).add(timeZoneOffset || 0, 'minutes'), days);
};

/** Return `from` and `to` params for relative queries in device local time
 * for data showing hourly data, where we are looking for 24 hours * x days worth of data
 */
export const getRelativeHourlyDeviceDateParams = (now: Moment, timeZoneOffset: number | null, days: number) => {
  return getHourlyParamsFromLocalNow(moment.utc(now).add(timeZoneOffset || 0, 'minutes'), days);
};

/** As getRelativeDailyDeviceDateParams, but the day boundaries are derived from an IANA zone
 * instead of a fixed offset, so ranges that span a DST transition stay on local calendar days.
 * The bounds are in the `local` representation (wall-clock readings tagged as UTC).
 */
export const getRelativeDailyTzDateParams = (now: Moment, timeZone: string, days: number) => {
  return getDailyParamsFromLocalNow(getZoneWallClock(now, timeZone), days);
};

/** As getRelativeHourlyDeviceDateParams, but derived from an IANA zone instead of a fixed offset. */
export const getRelativeHourlyTzDateParams = (now: Moment, timeZone: string, days: number) => {
  return getHourlyParamsFromLocalNow(getZoneWallClock(now, timeZone), days);
};

/** Return `from` and `to` params for relative queries in utc time
 * for data showing daily data, where we are looking for x days worth of data including today
 */
export const getRelativeDailyUtcDateParams = (now: Moment, days: number) => dependencies.getRelativeDailyDeviceDateParams(now, null, days);

/** Return `from` and `to` params for relative queries in utc time
 * for data showing hourly data, where we are looking for 24 hours * x days worth of data
 */
export const getRelativeHourlyUtcDateParams = (now: Moment, days: number) => dependencies.getRelativeHourlyDeviceDateParams(now, null, days);

export const dependencies = {
  getRelativeDailyDeviceDateParams,
  getRelativeHourlyDeviceDateParams,
};

/** Return `from` and `to` params for relative queries
 * for data showing hourly data, where we are looking for 24 hours * x days worth of data
 */
export const getTimePeriodParams = (days?: number) => {
  if (!days) {
    return undefined;
  }
  const now = moment();

  return {
    from: moment.utc(now).subtract(days * 24, 'hours').valueOf(),
    to: moment.utc(now).valueOf()
  };
};
